import { DndContext } from '@dnd-kit/core';
import { useAutoAnimate } from '~/common/hooks/useAutoAnimate';
import { Badge, type ModalProps } from '@mantine/core';
import { useElementSize } from '@mantine/hooks';
import type { TestType } from '@prisma/client';
import clsx from 'clsx';
import { useAtomValue } from 'jotai';
import type { KeyboardEventHandler } from 'react';
import { useMemo, useRef, useState, type PropsWithChildren } from 'react';
import { recorderTabsOpenAtom } from '~/atoms/recorder-atoms';
import type { RecorderCommand } from '~/common/recorder/recorder-operation';
import type { RecorderExecutionType } from '~/common/vm/shared-types';
import { getDeviceViewport } from '~/server/logic/driver-common/devices/devices';
import { TestTypeIcon } from '../test/TestTypeIcon';
import FloatingToolbar from './FloatingToolbar';
import ToolbarActions from './ToolbarActions';
import WebBrowserHeader from './WebBrowserHeader';

// Use this to display a modal inside the browser container
export const getBrowserModalProps = (epochId?: number): Partial<ModalProps> => ({
  portalProps: {
    target: `#browser-container${epochId ? `-${epochId}` : ''}`,
  },
  overlayProps: {
    fixed: false,
  },
  classNames: {
    inner: 'absolute',
  },
});

// Define approximate heights for calculation (can be refined)
const headerHeight = 40;
const tabsHeight = 20;
const footerHeight = 30;
const verticalPadding = 40;
const mobileSafetyMargin = 30; // Additional margin for mobile view

export default function Browser({
  children,
  executionType,
  sendCommand,
  aiActionButton,
  epochId,
  onKeyDown,
  onBrowserHover,
  testType,
  className,
  activeTab,
}: PropsWithChildren<{
  executionType?: RecorderExecutionType;
  sendCommand?: (command: RecorderCommand) => void;
  aiActionButton?: () => void;
  // We have to do this since otherwise modal creation/lining doesn't always work as we use this for the portal target
  // In certain cases where you restart the VM if you don't add this ID (that changes), then the modal is unable to
  // inject into or find the target
  epochId?: number;
  onKeyDown?: KeyboardEventHandler<HTMLDivElement | null>;
  onBrowserHover?: (hovered: boolean, browserElement: HTMLDivElement) => void;
  testType?: TestType;
  className?: string;
  activeTab?: string;
}>) {
  const [animationParent] = useAutoAnimate();
  // Using undefined to indicate the toolbar needs to be centered on first render
  // Once positioned by user drag, we store the actual pixel position
  const [toolbarXPosition, setToolbarPosition] = useState<number | undefined>(undefined);
  const [toolbarWidth, setToolbarWidth] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const { ref: wrapperRef, width: wrapperWidth, height: wrapperHeight } = useElementSize();

  // Get toolbar position (centered if not yet positioned by user)
  const effectiveToolbarPosition = useMemo(() => {
    if (toolbarXPosition === undefined && containerRef.current && toolbarWidth > 0) {
      return Math.floor((containerRef.current.offsetWidth - toolbarWidth) / 2);
    }
    return toolbarXPosition ?? 0;
  }, [toolbarXPosition, containerRef.current?.offsetWidth, toolbarWidth]);

  const recorderTabsOpen = useAtomValue(recorderTabsOpenAtom);

  const viewPort = useMemo(() => getDeviceViewport(testType), [testType]);

  const isMobile = testType === 'WEB_MOBILE' || testType === 'MOBILE_APP';
  const isWeb = testType === 'WEB' || testType === 'WEB_MOBILE';

  // Recalculate browser dimensions based on aspect ratio and container size
  const extraHeight = (isMobile ? headerHeight + footerHeight : headerHeight) + (recorderTabsOpen ? tabsHeight : 0);
  const aspectRatio = viewPort.width / viewPort.height;

  // Add mobile safety margin to prevent vertical overflow
  const effectiveVerticalPadding = isMobile ? verticalPadding + mobileSafetyMargin : verticalPadding;
  const maxAvailableHeight = wrapperHeight - effectiveVerticalPadding;

  const heightPrioritizedTriggerPoint = wrapperWidth / aspectRatio + effectiveVerticalPadding + extraHeight;
  const isHeightLimited = wrapperHeight < heightPrioritizedTriggerPoint && wrapperHeight > 0;

  // Calculate dimensions based on limiting factor (width or height)
  // For mobile, prioritize height constraints to prevent vertical overflow
  const browserHeight =
    isHeightLimited ?
      Math.min(maxAvailableHeight, wrapperHeight - effectiveVerticalPadding)
    : Math.min(Math.round((wrapperWidth - verticalPadding) / aspectRatio + extraHeight), maxAvailableHeight);

  const browserWidth =
    isHeightLimited ? Math.round((browserHeight - extraHeight) * aspectRatio) : wrapperWidth - verticalPadding;

  return (
    <div
      ref={wrapperRef}
      className={clsx('relative h-full overflow-hidden py-2', className)}
    >
      {(testType === 'WEB_MOBILE' || testType === 'MOBILE_APP') && (
        <Badge
          color="gray"
          variant="light"
          className="mt-4 ml-2 hidden normal-case xl:block"
        >
          <div className="flex items-center gap-1.5">
            <TestTypeIcon
              className="text-[var(--mantine-badge-color)] dark:text-[var(--mantine-badge-color)]"
              iconSize={14}
              type={testType}
              showText={true}
            />
            <span className="text-[9px] text-green-500">BETA</span>
          </div>
        </Badge>
      )}
      <div
        id={`browser-container${epochId ? `-${epochId}` : ''}`}
        className="absolute top-4 left-1/2 box-border flex max-h-[calc(100%-32px)] max-w-full shrink flex-col justify-center overflow-visible rounded-md bg-black"
        style={{
          width: browserWidth,
          height: browserHeight,
          // round to ensure pixel values are integers to avoid subpixel rendering issues
          transform: `translateX(${Math.round(-browserWidth / 2)}px)`,
        }}
      >
        {!!isWeb && (
          <WebBrowserHeader
            sendCommand={sendCommand}
            isMobile={isMobile}
            activeTab={activeTab}
          />
        )}
        {!!isMobile && (
          <div className="h-9 bg-[var(--mantine-color-gray-1)] dark:bg-[var(--mantine-color-dark-8)]">
            {executionType === 'record' && (
              <div className="animate-slowFadeIn flex h-full w-full items-center justify-center">
                <ToolbarActions
                  aiActionButton={aiActionButton}
                  isWeb={isWeb}
                />
              </div>
            )}
          </div>
        )}
        <div
          ref={animationParent}
          className={clsx(
            'box-border max-w-full flex-grow overflow-hidden border-2 border-gray-200 whitespace-nowrap shadow-sm outline-none dark:border-gray-700',
            isWeb ? 'rounded-b-md' : '',
          )}
          style={recorderTabsOpen ? undefined : { aspectRatio: `${viewPort.width}/${viewPort.height}` }}
          tabIndex={0}
          onKeyDown={onKeyDown}
          onMouseEnter={(e) => onBrowserHover?.(true, e.currentTarget)}
          onMouseLeave={(e) => onBrowserHover?.(false, e.currentTarget)}
        >
          {children}
          {executionType === 'record' && !isMobile && (
            <DndContext
              onDragEnd={(event) => {
                if (containerRef.current) {
                  const containerWidth = containerRef.current.offsetWidth;
                  const maxTranslate = containerWidth - toolbarWidth;

                  // Calculate and store final position after drag
                  setToolbarPosition(Math.max(0, Math.min(effectiveToolbarPosition + event.delta.x, maxTranslate)));
                }
              }}
            >
              <FloatingToolbar
                containerRef={containerRef}
                xPosition={effectiveToolbarPosition}
                aiActionButton={aiActionButton}
                onMount={(node) => {
                  if (node) {
                    setToolbarWidth(node.offsetWidth ?? 0);
                  }
                }}
              />
              <div
                className="invisible absolute top-0 left-0 h-0 w-full"
                ref={containerRef}
              />
            </DndContext>
          )}
        </div>
      </div>
    </div>
  );
}
